import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useParams, Prompt } from "react-router-dom";
import DocumentTitle from "react-document-title";
import { FaPlus, FaUndo } from "react-icons/fa";
import { FaSpinner, FaTrash, FaXmark } from "react-icons/fa6";

import SectionTable, { SectionHead, SectionBody } from "styles/components/SectionTable";
import Grid, { Col, Row } from "styles/components/Grid";
import { getAssessments, updateAssessment } from "services/assessment";
import { IMAGE_S3_SERVER, restApiBaseUrl } from "constants/api";

import IconSvgComponent from "components/common/IconSvgComponent";
import { FormattedText } from "../common/FormattedText";
import { FrameBox } from "styles/components/Frame";
import {
  StudentInfoQuestion,
  StudentInfoQuestionHead,
  StudentInfoQuestionList,
  StudentInfoQuestionListItem,
  StudentInfoQuestionListIndicator,
} from "styles/components/StudentInfo";
import {
  AssessmentQuestionNumber,
  AssessmentQuestionImage,
  AssessmentQuestionText,
  OptionImage,
  AnswerLabel,
  AnswerImage,
  AnswerText,
} from "styles/components/AssessmentQuestions";
import { addNotification, openModal } from "store/state/ui/actions";
import { LEVELS, createNotification } from "utilities/notification";
import { useDispatch } from "react-redux";
import { isEqual } from "lodash";
import { NoDataComponent, PaginatedTable } from "components/common/table";
import { getAccessToken } from "utilities/localStore";
import ButtonLink from "components/common/ButtonLink";
import stemifyRoutes from "constants/routes";
import { StyledSpan } from "styles/components/TableDefault";
import styled from "styled-components";
import Field from "components/common/Field";
import {
  Question,
  QuestionIcon,
  QuestionIconMeta,
  QuestionText,
  Circle,
} from "styles/components/NavigationQuestion";
import { Tab, TabContent, TabMenu } from "components/TabMenu";
import Loader from "components/common/loader";

const apiUrl = restApiBaseUrl;

const Button = styled.button`
  position: relative;
  color: white;
  border-radius: 5px;
  padding: 6px 12px;
  outline: none;
  border: 1px solid #4e68eb;
  background-color: #4e68eb;
  box-shadow: 1px 3px 3px -1px #888;
  transition: background-color 150ms linear;
  min-width: ${(props) => {
    const xPadding = 24;
    const contentLength = Math.max(props.children.length, props.loadingText?.length ?? 0);
    const minWidth = xPadding + contentLength * 8;

    return `${minWidth}px`;
  }};

  :hover {
    background-color: #1f49e6;
    border-color: #1f49e6;
  }

  :active {
    box-shadow: 0px 2px 3px -2px #888;
  }

  &[disabled],
  &[disabled]:hover,
  &[disabled]:active {
    cursor: not-allowed;
    opacity: 0.7;
    border: 1px solid #4e68eb;
    background-color: #4e68eb;
    box-shadow: 1px 3px 3px -1px #888;
  }

  ${(props) =>
    props.variant === "outlined" &&
    `
      color: #333;
      background-color: #fff;
      border: 1px solid #4e68eb;
      box-shadow: 1px 3px 3px -2px #4e68eb;

      :hover {
        background-color: #d7edff;
        border-color: #1f49e6;
      }

      :active {
        box-shadow: 0px 2px 3px -3px #4e68eb;
      }
  `}

  ${(props) =>
    props.variant === "icon" &&
    `
      padding: 6px 8px;
      padding-bottom: 3px;
      color: #333;
      background-color: #fff;
      border: 1px solid #4e68eb;
      box-shadow: 1px 3px 3px -2px #4e68eb;


      :hover {
        background-color: #d7edff;
        border-color: #1f49e6;
      }

      :active {
        box-shadow: 0px 2px 3px -3px #4e68eb;
      }

      &[disabled],
      &[disabled]:hover,
      &[disabled]:active {
        cursor: not-allowed;
        opacity: 0.7;
        color: #333;
        background-color: #fff;
        border: 1px solid #4e68eb;
        box-shadow: 1px 3px 3px -2px #4e68eb;
      }
  `}

  &::after {
    content: ${(props) => props.loadingText && `"${props.loadingText}"`};
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
  }

  ${(props) =>
    props.isLoading &&
    `
      pointer-events: none;
      background-color: #1cc379 !important;
      border: 1px solid #1cc379 !important;

      &::after {
        background-color: #1cc379;
        opacity: 1;
      }
  `}
`;

const AssessmentNameForm = ({ name = "", saveName, isSavingName }) => {
  const [draftName, setDraftName] = useState(name);

  useEffect(() => {
    setDraftName(name);
  }, [name]);

  return (
    <div
      style={{
        minWidth: "300px",
        width: "fit-content",
        margin: "15px auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
      }}
    >
      <Field
        id="name"
        name="name"
        value={draftName}
        onChange={(event) => setDraftName(event.target.value)}
        placeholder="Assessment Name"
        style={{ flexGrow: "1" }}
        autoComplete="off"
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Button
          variant="icon"
          disabled={name === draftName}
          onClick={(event) => setDraftName(name)}
        >
          <FaUndo />
        </Button>
        <Button
          disabled={name === draftName}
          onClick={() => saveName(draftName)}
          loadingText="Saving..."
          isLoading={isSavingName}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

const QuestionDescriptionForm = ({ description = [], updateDescription }) => {
  const handleTypeChange = (index) => (event) => {
    const { value } = event.target;

    updateDescription((previous) => {
      return previous.map((block, i) => {
        if (i === index) {
          return {
            text: "",
            src: "",
            ...block,
            type: value,
          };
        }

        return block;
      });
    });
  };

  const handleTextChange = (index) => (event) => {
    const { value } = event.target;

    updateDescription((previous) => {
      return previous.map((block, i) => {
        if (i === index) {
          return {
            ...block,
            text: value,
          };
        }

        return block;
      });
    });
  };

  const handleImageSrcChange = (index) => (event) => {
    const { value } = event.target;

    updateDescription((previous) => {
      return previous.map((block, i) => {
        if (i === index) {
          return {
            ...block,
            src: value,
          };
        }

        return block;
      });
    });
  };

  const addBlock = () => {
    updateDescription((previous) => {
      return previous.concat({
        type: "TEXT",
        src: "",
        text: "",
      });
    });
  };

  const removeBlock = () => {
    updateDescription((previous) => {
      return [previous[0]];
    });
  };

  return (
    <div>
      {description.map((content, index) => {
        return (
          <div key={index} style={{ marginBottom: "10px" }}>
            <TypeSelect
              value={content.type}
              onChange={handleTypeChange(index)}
              style={{ marginBottom: "10px" }}
            />
            {content.type === "TEXT" ? (
              <Field
                isTextarea
                id="content"
                name="content"
                placeholder="Content"
                value={content.text}
                onChange={handleTextChange(index)}
                rows="10"
                style={{ width: "100%" }}
              />
            ) : (
              <div style={{ display: "flex", alignItems: "flex-end", gap: "8px" }}>
                <ImagePrefix />
                <Field
                  type="text"
                  id="content"
                  name="content"
                  placeholder="Image URL"
                  value={content.src}
                  onChange={handleImageSrcChange(index)}
                  style={{
                    flexGrow: "1",
                    width: "100%",
                  }}
                />
              </div>
            )}
          </div>
        );
      })}

      {description.length < 2 && (
        <Button variant="outlined" onClick={addBlock}>
          Add Block
        </Button>
      )}

      {description.length === 2 && (
        <Button variant="outlined" onClick={removeBlock}>
          Remove Block
        </Button>
      )}
    </div>
  );
};

const QuestionAnswersForm = ({ answers = [], updateAnswers }) => {
  const handleTypeChange = (index) => (event) => {
    const { value } = event.target;

    updateAnswers((previous) => {
      return previous.map((answer, i) => {
        if (i === index) {
          return {
            ...answer,
            description: answer.description.map((block, blockIndex) => {
              if (blockIndex === 0) {
                return {
                  text: "",
                  src: "",
                  ...block,
                  type: value,
                };
              }

              return block;
            }),
          };
        }

        return answer;
      });
    });
  };

  const handleTextChange = (index) => (event) => {
    const { value } = event.target;

    updateAnswers((previous) => {
      return previous.map((answer, i) => {
        if (i === index) {
          return {
            ...answer,
            description: answer.description.map((block, blockIndex) => {
              if (blockIndex === 0) {
                return {
                  ...block,
                  text: value,
                };
              }

              return block;
            }),
          };
        }

        return answer;
      });
    });
  };

  const handleImageSrcChange = (index) => (event) => {
    const { value } = event.target;

    updateAnswers((previous) => {
      return previous.map((answer, i) => {
        if (i === index) {
          return {
            ...answer,
            description: answer.description.map((block, blockIndex) => {
              if (blockIndex === 0) {
                return {
                  ...block,
                  src: value,
                };
              }

              return block;
            }),
          };
        }

        return answer;
      });
    });
  };

  const handleCorrectChange = (index) => (event) => {
    const { checked } = event.target;

    updateAnswers((previous) => {
      return previous.map((answer, i) => {
        if (i === index) {
          return {
            ...answer,
            is_correct: checked,
          };
        }

        return answer;
      });
    });
  };

  const addAnswer = () => {
    updateAnswers((previous) => {
      return previous.concat({
        id: `new-${previous.length + 1}`,
        isNew: true,
        is_correct: false,
        display_order: previous.length + 1,
        description: [{ type: "TEXT", src: "", text: "" }],
      });
    });
  };

  const removeAnswer = (index) => () => {
    updateAnswers((previous) => {
      return previous.filter((a, i) => i !== index);
    });
  };

  return (
    <div>
      {answers.map((answer, answerIndex) => {
        return (
          <div key={answer.id} style={{ marginBottom: "15px" }}>
            <strong style={{ marginBottom: "6px" }} title={`ID: ${answer.id}`}>
              Answer {answerIndex + 1}
            </strong>

            {answer.description.map((content, contentIndex) => {
              return (
                <div key={contentIndex} style={{ marginBottom: "10px" }}>
                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingRight: "10px",
                      paddingBottom: "10px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "16px",
                      }}
                    >
                      <TypeSelect value={content.type} onChange={handleTypeChange(answerIndex)} />
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "4px",
                          width: "fit-content",
                        }}
                      >
                        <label style={{ fontSize: "12px" }}>Correct</label>
                        <input
                          type="checkbox"
                          name="is_correct"
                          checked={answer.is_correct}
                          onChange={handleCorrectChange(answerIndex)}
                          style={{
                            height: "16px",
                            width: "16px",
                          }}
                        />
                      </div>
                    </div>
                    {/* {answers.length > 1 && (
                      <FaTrash onClick={removeAnswer(answerIndex)} style={{ color: "gray" }} />
                    )} */}
                  </div>

                  {content.type === "TEXT" ? (
                    <Field
                      isTextarea
                      id="content"
                      name="content"
                      placeholder="Content"
                      value={content.text}
                      onChange={handleTextChange(answerIndex)}
                      rows="10"
                      style={{ width: "100%" }}
                    />
                  ) : (
                    <div style={{ display: "flex", alignItems: "flex-end", gap: "8px" }}>
                      <ImagePrefix />
                      <Field
                        type="text"
                        id="content"
                        name="content"
                        placeholder="Image URL"
                        value={content.src}
                        onChange={handleImageSrcChange(answerIndex)}
                        style={{
                          flexGrow: "1",
                          width: "100%",
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {answers.length < 6 && (
        <Button variant="outlined" onClick={addAnswer}>
          Add Answer
        </Button>
      )}
    </div>
  );
};

const QuestionTagsForm = ({ allTags = [], tags = [], updateTags }) => {
  const handleChangeByDepth = (depth) => (selectedOptions) => {
    const selectedIds = selectedOptions.map((o) => o.id);

    updateTags((previous) => {
      const newTags = selectedOptions
        .filter((option) => !previous.find((tag) => tag.id === option.id))
        .map((option) => allTags.find((tag) => tag.id === option.id));

      return previous
        .filter((tag) => {
          if (tag.depth === depth) {
            return selectedIds.includes(tag.id);
          }

          return true;
        })
        .concat(newTags);
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Field
        isSelect
        isMultiselect
        id="tags_depth_1"
        name="tags_depth_1"
        placeholder="Depth 1"
        value={tags
          .filter((t) => t.depth === 1)
          .map((tag) => {
            return {
              id: tag.id,
              value: tag.id,
              label: tag.name,
            };
          })}
        options={allTags
          .filter((t) => t.depth === 1)
          .map((tag) => {
            return {
              id: tag.id,
              value: tag.id,
              label: tag.name,
            };
          })}
        onChange={handleChangeByDepth(1)}
      />
      <Field
        isSelect
        isMultiselect
        id="tags_depth_2"
        name="tags_depth_2"
        placeholder="Depth 2"
        value={tags
          .filter((t) => t.depth === 2)
          .map((tag) => {
            return {
              id: tag.id,
              value: tag.id,
              label: tag.name,
            };
          })}
        options={allTags
          .filter((t) => t.depth === 2)
          .map((tag) => {
            return {
              id: tag.id,
              value: tag.id,
              label: tag.name,
            };
          })}
        onChange={handleChangeByDepth(2)}
      />
      <Field
        isSelect
        isMultiselect
        id="tags_depth_3"
        name="tags_depth_3"
        placeholder="Depth 3"
        value={tags
          .filter((t) => t.depth === 3)
          .map((tag) => {
            return {
              id: tag.id,
              value: tag.id,
              label: tag.name,
            };
          })}
        options={allTags
          .filter((t) => t.depth === 3)
          .map((tag) => {
            return {
              id: tag.id,
              value: tag.id,
              label: tag.name,
            };
          })}
        onChange={handleChangeByDepth(3)}
      />
    </div>
  );
};

const getDisplayType = (data) => {
  let display = "radio";
  if (data.find((i) => i.type === "IMAGE") != undefined) {
    display = "image";
  }
  return display;
};

const TypeSelect = ({ value, onChange, style = {} }) => {
  const options = [
    { id: "TEXT", value: "TEXT", label: "TEXT" },
    { id: "IMAGE", value: "IMAGE", label: "IMAGE" },
  ];

  return (
    <div style={style}>
      <Field
        isSelect
        isClearable={false}
        id="type"
        name="type"
        value={options.find((o) => o.value === value)}
        onChange={(selectedOption) => {
          onChange({ target: { name: "type", value: selectedOption.value } });
        }}
        options={options}
        placeholder="Type"
        style={{ minWidth: "150px", width: "fit-content" }}
      />
    </div>
  );
};

const ImagePrefix = () => {
  return (
    <div
      style={{
        backgroundColor: "lightgray",
        borderRadius: "2px",
        padding: "4px 4px 7px 6px",
        marginBottom: "2px",
      }}
    >
      https://images.savkar.ai/
    </div>
  );
};

const AdminPage = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const [tags, setTags] = useState([]);
  const [assessment, setAssessment] = useState({});
  const [draftAssessment, setDraftAssessment] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(""); // "" || "name" | "question" | "all"
  const [activeQuestionTab, setActiveQuestionTab] = useState("general");
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);

  const handleSelectQuestion = (questionId) => {
    setSelectedQuestionId(questionId);
  };

  const handleCreateQuestion = () => {
    const draftId = `new-${draftAssessment.questions.length + 1}`;

    setDraftAssessment((previous) => {
      return {
        ...previous,
        questions: previous.questions.concat({
          id: draftId,
          isNew: true,
          description: [
            {
              type: "TEXT",
              text: "Enter question here...",
            },
          ],
          answers: [
            {
              id: "new-1",
              display_order: 1,
              isNew: true,
              is_correct: false,
              description: [
                {
                  type: "TEXT",
                  text: "Enter answer 1 here",
                },
              ],
            },
            {
              id: "new-2",
              display_order: 2,
              isNew: true,
              is_correct: false,
              description: [
                {
                  type: "TEXT",
                  text: "Enter answer 2 here",
                },
              ],
            },
            {
              id: "new-3",
              display_order: 3,
              isNew: true,
              is_correct: true,
              description: [
                {
                  type: "TEXT",
                  text: "It's always answer C",
                },
              ],
            },
            {
              id: "new-4",
              display_order: 4,
              isNew: true,
              is_correct: false,
              description: [
                {
                  type: "TEXT",
                  text: "Enter answer 4 here",
                },
              ],
            },
          ],
          tags: [],
          assessment_content: {
            display_order: previous.questions.length + 1,
          },
        }),
      };
    });
    handleSelectQuestion(draftId);
  };

  const handleQuestionChange = (changeFunction) => {
    setDraftAssessment((previous) => {
      return {
        ...previous,
        questions: previous.questions.map((q) => {
          if (q.id === selectedQuestionId) {
            if (activeQuestionTab === "general") {
              return {
                ...q,
                description: changeFunction(q.description),
              };
            }

            if (activeQuestionTab === "answers") {
              return {
                ...q,
                answers: changeFunction(q.answers),
              };
            }

            if (activeQuestionTab === "tags") {
              return {
                ...q,
                tags: changeFunction(q.tags),
              };
            }

            return q;
          }

          return q;
        }),
      };
    });
  };

  const fetchAssessment = async () => {
    setIsLoading(true);

    try {
      const result = await fetch(`${apiUrl}/rest/assessments/${params.assessmentId}`, {
        mode: "cors",
        method: "get",
        headers: {
          Authorization: getAccessToken(),
          "Content-Type": "application/json",
        },
      }).then((response) => response.json());

      setAssessment(result.data);
      setDraftAssessment({
        questions: result.data.questions,
      });
    } catch (error) {
      console.log({ error });
    }
    setIsLoading(false);
  };

  const fetchTags = async () => {
    try {
      const result = await fetch(`${apiUrl}/rest/tags`, {
        mode: "cors",
        method: "get",
        headers: {
          Authorization: getAccessToken(),
          "Content-Type": "application/json",
        },
      }).then((response) => response.json());

      setTags(result.data);
    } catch (error) {
      console.log({ error });
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  useEffect(() => {
    fetchAssessment();
  }, [params.assessmentId]);

  const hasQuestions = assessment?.questions?.length > 0;

  const questionsWithChanges = useMemo(() => {
    if (!assessment?.questions || !draftAssessment?.questions) return [];

    let allQuestionChanges = [];
    draftAssessment.questions.forEach((draftQuestion, index) => {
      let questionChanges = [];
      const serverQuestion = assessment.questions.find(
        (serverQuestion) => serverQuestion.id === draftQuestion.id
      );

      if (!serverQuestion) {
        allQuestionChanges.push({
          id: draftQuestion.id,
          changes: ["general", "answers", "tags"],
        });
        return;
      }

      const draftDescription = draftQuestion.description;
      const serverDescription = serverQuestion.description;

      const hasDescriptionChange =
        draftDescription.length !== serverDescription.length ||
        draftDescription.some((draftBlock, index) => {
          const serverBlock = serverDescription[index];

          const hasBlock = !!serverBlock;
          if (!hasBlock) {
            return true;
          }

          const hasSameType = draftBlock.type === serverBlock.type;
          if (!hasSameType) {
            return true;
          }

          if (draftBlock.type === "TEXT") {
            return draftBlock.text !== serverBlock.text;
          }

          if (draftBlock.type === "IMAGE") {
            return draftBlock.src !== serverBlock.src;
          }

          return false;
        });

      const draftAnswers = draftQuestion.answers;
      const serverAnswers = serverQuestion.answers;

      const hasAnswerChanges =
        draftAnswers.length !== serverAnswers.length ||
        draftAnswers.some((draftAnswer, index) => {
          const serverAnswer = serverAnswers[index];

          const hasAnswer = !!serverAnswer;
          if (!hasAnswer) {
            return true;
          }

          const hasSameCorrectness = draftAnswer.is_correct === serverAnswer.is_correct;
          if (!hasSameCorrectness) {
            return true;
          }

          const serverAnswerDescription = serverAnswers[index].description[0];
          const draftAnswerDescription = draftAnswer.description[0];

          const hasSameType = draftAnswerDescription.type === serverAnswerDescription.type;
          if (!hasSameType) {
            return true;
          }

          if (draftAnswerDescription.type === "TEXT") {
            return draftAnswerDescription.text !== serverAnswerDescription.text;
          }

          if (draftAnswerDescription.type === "IMAGE") {
            return draftAnswerDescription.src !== serverAnswerDescription.src;
          }

          return false;
        });

      const draftTags = draftQuestion.tags;
      const draftTagIds = draftTags.map((tag) => tag.id);
      const serverTags = serverQuestion.tags;
      const serverTagIds = serverTags.map((tag) => tag.id);

      const draftHasAllServerTags = serverTagIds.every((id) => draftTagIds.includes(id));
      const serverHasAllDraftTags = draftTagIds.every((id) => serverTagIds.includes(id));

      if (hasDescriptionChange) {
        questionChanges.push("general");
      }

      if (hasAnswerChanges) {
        questionChanges.push("answers");
      }

      if (!draftHasAllServerTags || !serverHasAllDraftTags) {
        questionChanges.push("tags");
      }

      if (questionChanges.length > 0) {
        allQuestionChanges.push({
          id: draftQuestion.id,
          changes: questionChanges,
        });
      }
    });

    return allQuestionChanges;
  }, [assessment, draftAssessment]);

  const tabsWithChanges = useMemo(() => {
    const selectedQuestionHasChanges = questionsWithChanges.some(
      (q) => q.id === selectedQuestionId
    );

    if (selectedQuestionHasChanges) {
      const changedQuestion = questionsWithChanges.find((q) => q.id === selectedQuestionId);
      const changeFields = changedQuestion.changes;

      let tabChanges = [];
      const hasGeneralChanges = changeFields.includes("general");
      const hasAnswerChanges = changeFields.includes("answers");
      const hasTagChanges = changeFields.includes("tags");

      if (hasGeneralChanges) {
        tabChanges.push("general");
      }

      if (hasAnswerChanges) {
        tabChanges.push("answers");
      }

      if (hasTagChanges) {
        tabChanges.push("tags");
      }

      return tabChanges;
    }

    return [];
  }, [questionsWithChanges, selectedQuestionId]);

  const selectedQuestion = useMemo(() => {
    return draftAssessment?.questions?.find((q) => q.id === selectedQuestionId) ?? null;
  }, [draftAssessment, selectedQuestionId]);

  const notify = (level, message) => {
    switch (level) {
      case LEVELS.ERROR:
        return dispatch(
          addNotification({
            notification: createNotification(
              LEVELS.ERROR,
              "Error",
              message ?? "Encountered an issue completing action."
            ),
          })
        );

      case LEVELS.SUCCESS:
      default:
        return dispatch(
          addNotification({
            notification: createNotification(
              LEVELS.SUCCESS,
              "Success",
              message ?? "Action completed successfully!"
            ),
          })
        );
    }
  };

  const handleSaveName = async (name) => {
    setIsSaving("name");

    try {
      const result = await fetch(`${apiUrl}/rest/assessments/${params.assessmentId}`, {
        mode: "cors",
        method: "put",
        headers: {
          Authorization: getAccessToken(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name }),
      }).then((response) => response.json());

      setAssessment((previous) => {
        return {
          ...previous,
          name: result.data.name,
        };
      });

      notify(LEVELS.SUCCESS, "Name updated successfully!");
    } catch (error) {
      console.error(error);
      notify(LEVELS.ERROR, "Encountered an issue while saving assessment name.");
    }

    setIsSaving("");
  };

  const handleSaveQuestion = async () => {
    setIsSaving("question");

    try {
      const body = {
        questions: [
          {
            id: selectedQuestionId,
            isNew: selectedQuestion.isNew,
            display_order: selectedQuestion.assessment_content.display_order,
            description: selectedQuestion.description,
            answers: selectedQuestion.answers.map((answer) => {
              return {
                id: answer.isNew ? "" : answer.id,
                isNew: answer.isNew,
                // TODO: deleting answers
                // isDeleted: answer.isNew,
                description: answer.description,
                display_order: answer.display_order,
                is_correct: answer.is_correct,
              };
            }),
            tagIds: selectedQuestion.tags.map((tag) => tag.id),
          },
        ],
      };

      const result = await fetch(`${apiUrl}/rest/assessments/${params.assessmentId}`, {
        mode: "cors",
        method: "put",
        headers: {
          Authorization: getAccessToken(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      }).then((response) => response.json());

      if (result.status === "error") {
        throw Error(result.message);
      }

      setAssessment(result.data);

      const matchingQuestion =
        result.data.questions[selectedQuestion.assessment_content.display_order - 1];

      console.log({ matchingQuestion, result });

      setDraftAssessment((previous) => {
        return {
          ...previous,
          questions: previous.questions.map((draftQuestion) => {
            if (draftQuestion.id === selectedQuestionId) {
              return matchingQuestion;
            }

            return draftQuestion;
          }),
        };
      });

      if (selectedQuestion.isNew) {
        setSelectedQuestionId(matchingQuestion.id);
      }

      notify(LEVELS.SUCCESS, "Question updated successfully!");
    } catch (error) {
      console.error(error);
      notify(LEVELS.ERROR, "Encountered an issue while updating question.");
    }

    setIsSaving("");
  };

  const handleSaveAllChanges = async (name) => {
    setIsSaving("all");

    try {
      const result = await fetch(`${apiUrl}/rest/assessments/${params.assessmentId}`, {
        mode: "cors",
        method: "put",
        headers: {
          Authorization: getAccessToken(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name }),
      }).then((response) => response.json());

      setAssessment((previous) => {
        return {
          ...previous,
          name: result.data.name,
        };
      });

      notify(LEVELS.SUCCESS, "All changes updated successfully!");
    } catch (error) {
      console.error(error);
      notify(LEVELS.ERROR, "Encountered an issue while saving all changes.");
    }

    setIsSaving("");
  };

  return (
    <DocumentTitle title="Stemify | Administration">
      <SectionTable
        style={{ padding: "15px", paddingRight: "0px", paddingBottom: "30px", flexGrow: 1 }}
      >
        <Prompt
          when={questionsWithChanges.length > 0}
          message={(location) =>
            `You will lose all unsaved changes on this page, are you sure you want to leave?`
          }
        />

        <SectionHead style={{ marginBottom: "10px" }}>
          <h2 style={{ textAlign: "center", marginBottom: "0px" }}>Assessment Manager</h2>

          {isLoading && <Loader />}
          {!isLoading && (
            <>
              <AssessmentNameForm
                name={assessment.name}
                saveName={handleSaveName}
                isSavingName={isSaving === "name"}
              />
              <div style={{ maxWidth: "70%", margin: "0px auto" }}>
                <div style={{ textAlign: "center" }}>
                  {!hasQuestions && <div>This assessments has no questions</div>}
                </div>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "10px",
                  }}
                >
                  {draftAssessment?.questions?.map((q) => {
                    const isActiveQuestion = q.id === selectedQuestionId;

                    return (
                      <Question
                        key={q.id}
                        title={`ID: ${q.id}`}
                        style={{ position: "relative", height: "fit-content" }}
                        onClick={() => handleSelectQuestion(q.id)}
                      >
                        <QuestionIcon
                          hasIndicator={questionsWithChanges.some(
                            (changedQuestion) => changedQuestion.id === q.id
                          )}
                          style={{
                            marginBottom: "0px",
                            border: "2px solid #1f49e6",
                            borderRadius: "999px",
                            backgroundColor: isActiveQuestion ? "#4e68eb" : "#fff",
                          }}
                        />
                        <QuestionText
                          style={{
                            position: "absolute",
                            top: "0px",
                            left: "0px",
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: isActiveQuestion ? "#fff" : "#333",
                          }}
                        >
                          {q.assessment_content.display_order}
                        </QuestionText>
                      </Question>
                    );
                  })}
                  <Question
                    title="Create question"
                    style={{ position: "relative", height: "fit-content" }}
                    onClick={handleCreateQuestion}
                  >
                    <QuestionIcon
                      style={{
                        marginBottom: "0px",
                        border: "2px solid #1cc379",
                        borderRadius: "999px",
                        backgroundColor: "#fff",
                      }}
                    />
                    <QuestionText
                      style={{
                        position: "absolute",
                        top: "0px",
                        left: "0px",
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#1cc379",
                      }}
                    >
                      <FaPlus />
                    </QuestionText>
                  </Question>
                </div>

                {/* {questionsWithChanges.length > 1 && (
                  <Button
                    onClick={handleSaveAllChanges}
                    style={{
                      margin: "0 auto",
                      display: "block",
                    }}
                  >
                    Save All Changes
                  </Button>
                )} */}
              </div>
            </>
          )}
        </SectionHead>

        {selectedQuestion && (
          <div style={{ flexGrow: 1, display: "flex", gap: "12px" }}>
            <SectionBody
              style={{
                flex: "none",
                width: "600px",
                padding: "24px 32px",
                overflow: "auto",
              }}
            >
              <h4>Question {selectedQuestion.assessment_content.display_order}</h4>
              <div
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
              >
                <TabMenu>
                  <Tab
                    value="general"
                    activeTab={activeQuestionTab}
                    setActiveTab={setActiveQuestionTab}
                    hasIndicator={tabsWithChanges.includes("general")}
                  >
                    General
                  </Tab>
                  <Tab
                    value="answers"
                    activeTab={activeQuestionTab}
                    setActiveTab={setActiveQuestionTab}
                    hasIndicator={tabsWithChanges.includes("answers")}
                  >
                    Answers
                  </Tab>
                  <Tab
                    value="tags"
                    activeTab={activeQuestionTab}
                    setActiveTab={setActiveQuestionTab}
                    hasIndicator={tabsWithChanges.includes("tags")}
                  >
                    Tags
                  </Tab>
                </TabMenu>
                <Button
                  disabled={isLoading || tabsWithChanges.length === 0}
                  onClick={handleSaveQuestion}
                >
                  {isLoading ? "Saving..." : "Save"}
                </Button>
              </div>

              <TabContent value="general" activeTab={activeQuestionTab}>
                <QuestionDescriptionForm
                  description={selectedQuestion.description}
                  updateDescription={handleQuestionChange}
                />
              </TabContent>

              <TabContent value="answers" activeTab={activeQuestionTab}>
                <QuestionAnswersForm
                  answers={selectedQuestion.answers}
                  updateAnswers={handleQuestionChange}
                />
              </TabContent>

              <TabContent value="tags" activeTab={activeQuestionTab}>
                <QuestionTagsForm
                  allTags={tags}
                  tags={selectedQuestion.tags}
                  updateTags={handleQuestionChange}
                />
              </TabContent>
            </SectionBody>

            <SectionBody
              style={{
                flexGrow: 1,
                height: "fit-content",
                padding: "8px 16px",
                overflow: "auto",
              }}
            >
              <FrameBox style={{ border: "none", boxShadow: "none" }}>
                <StudentInfoQuestion>
                  <StudentInfoQuestionHead>
                    <AssessmentQuestionImage fullwidth style={{ textAlign: "left" }}>
                      {selectedQuestion.description.map((dataItem, k) => {
                        if (dataItem.type === "TEXT") {
                          return (
                            <AssessmentQuestionText
                              key={k}
                              layout={selectedQuestion.layout}
                              widthCounter={selectedQuestion.description.length}
                              floatCounter={k}
                              style={{ textAlign: "left" }}
                            >
                              {k == 0 && (
                                <AssessmentQuestionNumber style={{ textAlign: "left" }}>
                                  {selectedQuestion.assessment_content.display_order}.
                                </AssessmentQuestionNumber>
                              )}
                              <FormattedText text={dataItem.text} />
                            </AssessmentQuestionText>
                          );
                        }
                        return (
                          <AssessmentQuestionText
                            key={k}
                            layout={selectedQuestion.layout}
                            widthCounter={selectedQuestion.description.length}
                            floatCounter={k}
                          >
                            {k == 0 && (
                              <AssessmentQuestionNumber>
                                {selectedQuestion.assessment_content.display_order}.
                              </AssessmentQuestionNumber>
                            )}
                            <OptionImage
                              widthCounter={selectedQuestion.description.length}
                              layout={selectedQuestion.layout}
                            >
                              <img
                                key={k}
                                src={`${IMAGE_S3_SERVER}${dataItem.src}`}
                                alt={dataItem.src}
                              />
                            </OptionImage>
                          </AssessmentQuestionText>
                        );
                      })}
                    </AssessmentQuestionImage>
                  </StudentInfoQuestionHead>

                  <StudentInfoQuestionList>
                    {selectedQuestion.answers.map((answer, aIndex) => (
                      <StudentInfoQuestionListItem
                        key={aIndex}
                        correct={answer.is_correct}
                        selected={answer.isSelected}
                        containsImage={answer.description[0]?.type === "IMAGE"}
                      >
                        <StudentInfoQuestionListIndicator
                          correct={answer.is_correct}
                          selected={answer.isSelected}
                        >
                          {answer.is_correct && (
                            <IconSvgComponent
                              additionalClass="correct"
                              iconPath="svg/ico-correct.svg"
                            />
                          )}
                          {selectedQuestion.isAttempted &&
                            !selectedQuestion.is_correct &&
                            answer.isSelected && (
                              <IconSvgComponent
                                additionalClass="correct"
                                iconPath="svg/ico-incorrect.svg"
                              />
                            )}
                        </StudentInfoQuestionListIndicator>
                        <AnswerLabel
                          displayType={getDisplayType(answer.description)}
                          quizType="assessment"
                          layout={answer.layout}
                        >
                          <AnswerImage>
                            {answer.description &&
                              answer.description.map((data, k) => {
                                if (data.type === "TEXT") {
                                  return (
                                    <AnswerText
                                      key={k}
                                      layout={answer.layout}
                                      widthCounter={answer.description.length}
                                      floatCounter={k}
                                    >
                                      <FormattedText text={data.text}></FormattedText>
                                    </AnswerText>
                                  );
                                } else {
                                  return (
                                    <OptionImage
                                      key={k}
                                      widthCounter={answer.description.length}
                                      layout={answer.layout}
                                    >
                                      {data.src.trim() ? (
                                        <img
                                          key={k}
                                          src={`${IMAGE_S3_SERVER}${data.src}`}
                                          alt={data.src}
                                        />
                                      ) : null}
                                    </OptionImage>
                                  );
                                }
                              })}
                          </AnswerImage>
                        </AnswerLabel>
                      </StudentInfoQuestionListItem>
                    ))}
                  </StudentInfoQuestionList>
                </StudentInfoQuestion>
              </FrameBox>
            </SectionBody>
          </div>
        )}
      </SectionTable>
    </DocumentTitle>
  );
};

export default AdminPage;
